import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Snackbar } from '@mui/material';
import comms from '../comms';

// #3/#4 Route bidding — browse unclaimed future routes and claim one
// first-come-first-served, optionally extending the shift end.
// Surfaced from the dashboard "Claim Routes" tile; the tile is gated on the
// fleet's routeBidding flag (boolean `routeBidding: true`, or the legacy
// `routeBidding.enabled` / `bidding` forms). See Route Bidding guide §3–§5.

function fmtTime(v) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime())
    ? String(v)
    : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// Group-header label for a service date, e.g. "Mon, Jun 9".
function fmtServiceDate(v) {
  if (!v) return 'Unscheduled';
  // ServiceDate is YYYY-MM-DD — parse as local to avoid a UTC off-by-one.
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

// Convert a server datetime (local, no TZ) to a datetime-local input value
// (YYYY-MM-DDTHH:MM) — the exact shape claimRoute's endDateTime expects.
function toInputValue(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ClaimableRoutes({ userSettings }) {
  const [routes, setRoutes] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | loaded | error
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false); // feature not on this fleet (404)
  const [serviceDate, setServiceDate] = useState(''); // YYYY-MM-DD filter (optional)
  const [claimingId, setClaimingId] = useState(null);
  const [extend, setExtend] = useState({}); // routeId -> bool ("Work later?" toggle)
  const [endTimes, setEndTimes] = useState({}); // routeId -> datetime-local string
  const [toast, setToast] = useState(null);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const driverId =
    userSettings?.Id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;
  const currentVehicle = JSON.parse(localStorage.getItem('selectedVehicle') || 'null');
  const currentVehicleId = currentVehicle?.Id || currentVehicle?.id || null;

  // Offline safety (§3.1): a claim is FCFS and must be confirmed by the live
  // server, so it must never queue offline. Disable Claim and tell the driver.
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const params = {};
      if (serviceDate) params.serviceDate = serviceDate;
      const res = await comms.getClaimableRoutes(params);
      // The endpoint isn't deployed/enabled on this fleet (404) — comms returns
      // { available:false }. Show a friendly notice, not a list or an error.
      if (res && res.available === false) {
        setUnavailable(true);
        setRoutes([]);
        setStatus('loaded');
        return;
      }
      setUnavailable(false);
      setRoutes(Array.isArray(res?.routes) ? res.routes : []);
      setStatus('loaded');
    } catch (e) {
      // Never surface a raw axios string ("Request failed with status code …").
      setError('Could not load routes. Please try again.');
      setStatus('error');
    }
  }, [serviceDate]);

  useEffect(() => {
    load();
  }, [load]);

  // Group loaded routes by service date for sectioned rendering (§3.2).
  const grouped = useMemo(() => {
    const map = new Map();
    routes.forEach((r) => {
      const key = r.ServiceDate || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries()).sort(([a], [b]) => String(a).localeCompare(String(b)));
  }, [routes]);

  const handleClaim = async (route) => {
    if (!driverId) {
      setToast('Driver not identified.');
      return;
    }
    if (!online) {
      setToast('Connect to claim a route.');
      return;
    }

    // vehicleId is REQUIRED by the server (route-bidding-api.md §ClaimRoute) — the
    // claim stamps Trip.VehicleId too. Block early with a clear message rather than
    // letting the server reject the claim with a raw error.
    const selectedVehicle = JSON.parse(localStorage.getItem('selectedVehicle') || 'null');
    const vehicleId = selectedVehicle?.Id || selectedVehicle?.id || undefined;
    if (!vehicleId) {
      setToast('Select your vehicle before claiming a route.');
      return;
    }

    const routeId = route.RouteId;

    // Resolve the optional extended end. Only send it when the driver opted in
    // AND it is strictly later than the route's default end (§3.4 validation).
    let endDateTime;
    if (extend[routeId]) {
      const chosen = endTimes[routeId];
      const defaultEnd = toInputValue(route.EndDateTime);
      if (!chosen) {
        setToast('Pick the time you will work until, or turn off "Work later".');
        return;
      }
      if (defaultEnd && chosen <= defaultEnd) {
        setToast('Choose a time later than the default end.');
        return;
      }
      endDateTime = chosen;
    }

    setClaimingId(routeId);
    try {
      const res = await comms.claimRoute({ routeId, driverId, vehicleId, endDateTime });

      if (res?.status === true) {
        // Server is authoritative and assigns the route's trips during the claim
        // (route-bidding-api.md §ClaimRoute step 3), returning assignedTripCount.
        // Drop the claimed card; stay on the list.
        setRoutes((prev) => prev.filter((r) => r.RouteId !== routeId));
        const persisted = res.endDateTime || endDateTime;
        const n = res.assignedTripCount;
        const tripPart =
          n != null ? ` ${n} ${n === 1 ? 'trip' : 'trips'} added to your bookings.` : '';
        const whenPart = persisted
          ? `Route claimed — working until ${fmtTime(persisted)}.`
          : `Route claimed for ${fmtServiceDate(route.ServiceDate)}.`;
        setToast(`${whenPart}${tripPart}`);
        return;
      }
      if (res?.alreadyClaimed) {
        // Lost the race — never mark claimed from optimistic local state.
        setToast('Sorry, that route was just taken.');
        load();
        return;
      }
      setToast(res?.error || res?.message || "Couldn't claim route, try again.");
    } catch (e) {
      setToast(e?.message || "Couldn't claim route, try again.");
    } finally {
      setClaimingId(null);
    }
  };

  // #5 Release the route currently assigned to this driver's vehicle.
  const handleRelease = async () => {
    if (!currentVehicleId) return;
    setReleasing(true);
    try {
      const res = await comms.unassignVehicleFromRoute(null, currentVehicleId);
      if (res?.success) {
        setToast('Route released.');
        load();
      } else {
        setToast(res?.message || 'Could not release route.');
      }
    } catch (e) {
      setToast(e?.message || 'Could not release route.');
    } finally {
      setReleasing(false);
      setConfirmRelease(false);
    }
  };

  const renderCard = (route) => {
    const routeId = route.RouteId;
    const hasBreak = route.BreakStartDateTime || route.BreakEndDateTime;
    const isExtending = !!extend[routeId];
    const defaultEndInput = toInputValue(route.EndDateTime);
    const showMaster =
      route.RouteMasterName && route.RouteMasterName !== route.RouteName;
    return (
      <div key={routeId} className="rounded-2xl bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-gray-900">
            {route.RouteName || route.RouteMasterName || 'Route'}
          </span>
          {route.TripCount != null && (
            <span className="text-xs text-gray-500">
              {route.TripCount} {route.TripCount === 1 ? 'trip' : 'trips'}
            </span>
          )}
        </div>
        {showMaster && <div className="text-xs text-gray-500">{route.RouteMasterName}</div>}

        <div className="mt-1 space-y-0.5 text-sm text-gray-700">
          {(route.StartDateTime || route.EndDateTime) && (
            <div>
              {fmtTime(route.StartDateTime)}
              {route.EndDateTime ? ` – ${fmtTime(route.EndDateTime)}` : ''}
            </div>
          )}
          {hasBreak && (
            <div className="text-xs text-gray-500">
              Break {fmtTime(route.BreakStartDateTime)}
              {route.BreakEndDateTime ? ` – ${fmtTime(route.BreakEndDateTime)}` : ''}
            </div>
          )}
        </div>

        {/* Trip preview (route-bidding-api.md → route.trips[], ordered by pickup) */}
        {Array.isArray(route.trips) && route.trips.length > 0 && (
          <div className="mt-2 space-y-1 rounded-xl bg-gray-50 px-3 py-2">
            {route.trips.slice(0, 3).map((t) => (
              <div key={t.tripId} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-gray-700">
                  {t.passengerName || `Trip #${t.tripNo}`}
                  {t.pickupAddress ? <span className="text-gray-400"> · {t.pickupAddress}</span> : null}
                </span>
                <span className="shrink-0 text-gray-500">{fmtTime(t.pickupTime)}</span>
              </div>
            ))}
            {route.trips.length > 3 && (
              <div className="text-xs text-gray-400">+{route.trips.length - 3} more</div>
            )}
          </div>
        )}

        {/* Extend shift end (§3.4) — opt in, then pick a time later than default */}
        <div className="mt-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={isExtending}
              onChange={(e) => {
                const on = e.target.checked;
                setExtend((p) => ({ ...p, [routeId]: on }));
                // Seed the picker with the default end the first time it opens.
                setEndTimes((p) => ({ ...p, [routeId]: p[routeId] || defaultEndInput }));
              }}
            />
            <span>Work later than {fmtTime(route.EndDateTime) || 'the default end'}?</span>
          </label>
          {isExtending && (
            <input
              type="datetime-local"
              min={defaultEndInput || undefined}
              value={endTimes[routeId] || ''}
              onChange={(e) =>
                setEndTimes((prev) => ({ ...prev, [routeId]: e.target.value }))
              }
              className="mt-2 w-full rounded-lg border border-gray-200 px-2 py-1 text-sm"
            />
          )}
        </div>

        <button
          onClick={() => handleClaim(route)}
          disabled={claimingId === routeId || !online}
          className="mt-3 w-full rounded-xl bg-brand px-4 py-2 text-sm font-semibold text-white transition-transform active:scale-95 disabled:opacity-60"
        >
          {claimingId === routeId ? 'Claiming...' : !online ? 'Offline' : 'Claim'}
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Offline notice */}
      {!online && (
        <div className="rounded-2xl bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-800 shadow-sm">
          You are offline — connect to claim a route.
        </div>
      )}

      {/* Vehicle required to claim (route-bidding-api.md: vehicleId is required) */}
      {online && !unavailable && !currentVehicleId && (
        <div className="rounded-2xl bg-amber-50 px-4 py-3 text-center text-sm font-medium text-amber-800 shadow-sm">
          Select your vehicle to claim a route.
        </div>
      )}

      {/* #5 Release current route */}
      {currentVehicleId && (
        <div className="rounded-2xl bg-white px-4 py-3 shadow-sm">
          {!confirmRelease ? (
            <button
              onClick={() => setConfirmRelease(true)}
              className="w-full rounded-xl border border-danger px-4 py-2 text-sm font-semibold text-danger transition-transform active:scale-95"
            >
              Release my current route
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">
                Release the route assigned to your vehicle? This frees it for other drivers.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmRelease(false)}
                  disabled={releasing}
                  className="flex-1 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRelease}
                  disabled={releasing}
                  className="flex-1 rounded-xl bg-danger px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {releasing ? 'Releasing...' : 'Release'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Feature not enabled / not deployed on this fleet (404) */}
      {unavailable && (
        <div className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
          Route bidding isn&apos;t available for your fleet right now.
        </div>
      )}

      {/* Date filter */}
      {!unavailable && (
      <div className="flex items-center gap-2 rounded-2xl bg-white px-4 py-3 shadow-sm">
        <label className="text-sm text-gray-500">Service date</label>
        <input
          type="date"
          value={serviceDate}
          onChange={(e) => setServiceDate(e.target.value)}
          className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-sm"
        />
        {serviceDate && (
          <button
            onClick={() => setServiceDate('')}
            className="text-sm font-semibold text-brand"
          >
            Clear
          </button>
        )}
      </div>
      )}

      {status === 'loading' && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-gray-200" />
          ))}
        </div>
      )}

      {status === 'error' && (
        <div className="rounded-2xl bg-white px-4 py-6 text-center text-sm text-gray-500 shadow-sm">
          {error}
        </div>
      )}

      {status === 'loaded' && !unavailable && !routes.length && (
        <div className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
          No upcoming routes available to claim.
        </div>
      )}

      {status === 'loaded' &&
        grouped.map(([date, list]) => (
          <div key={date || 'none'} className="space-y-3">
            <h2 className="px-1 text-sm font-bold text-gray-700">{fmtServiceDate(date)}</h2>
            {list.map((route) => renderCard(route))}
          </div>
        ))}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
}

export default ClaimableRoutes;
